import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, CreditCard, Package } from 'lucide-react';
import { cartService, orderService } from '../mock';
import { toast } from 'sonner';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    city: ''
  });
  const [loading, setLoading] = useState(false);

  // Scroll to top when component mounts - multiple methods for reliability
  React.useEffect(() => {
    // Method 1: Immediate scroll
    window.scrollTo(0, 0);
    
    // Method 2: Smooth scroll after a tiny delay
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 50);
    
    // Method 3: Force scroll after component render
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // --- Validation first ---
    if (!formData.fullName || !formData.email || !formData.phone || !formData.city) {
      toast.error('Please fill all required fields');
      return; // STOP here if validation fails
    }

    setLoading(true);

    try {
      // --- Google Sheets URL ---
      const googleSheetsUrl = "https://script.google.com/macros/s/AKfycbxOZkwovgHbbaMSUhUpyjeoBdxl0TiNAx5tmRwBSG7j3658i39kCfLOPCX7e4JFEgjc/exec";

      const params = new URLSearchParams({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        city: formData.city
      });

      // Google Sheets fetch (no-cors, can't reliably catch errors)
      fetch(`${googleSheetsUrl}?${params.toString()}`, {
        method: 'GET',
        mode: 'no-cors'
      });

      const order = orderService.createOrder({
        customer: formData,
        waitlist: true
      });
      orderService.updateOrderStatus(order.id, 'waitlist-confirmed');

      toast.success('Successfully joined the waitlist!');
      navigate(`/order-confirmation/${order.id}`);

    } catch (error) {
      console.error('Error submitting waitlist:', error);
      toast.error('Failed to join waitlist. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <Button 
          variant="ghost" 
          className="mb-6 hover:bg-gray-100"
          onClick={() => navigate('/')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        <div className="max-w-2xl mx-auto">
          {/* Waitlist Form */}
          <Card className="shadow-lg border-0">
            <CardHeader className="text-center pb-6">
              <div className="bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-medium inline-block mb-4">
                Join the Waitlist
              </div>
              <CardTitle className="text-3xl">Get Early Access to Baitho</CardTitle>
              <p className="text-gray-600 mt-3">
                Register now for early access. We'll confirm your spot within a few days and ship your deck in December — just in time for the holidays!
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Your Information</h3>
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input 
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input 
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input 
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+91 98765 43210"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input 
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="Mumbai, Delhi, Bangalore, etc."
                      required
                    />
                  </div>
                </div>

                <Separator />

                <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded">
                  <h4 className="font-semibold text-gray-900 mb-2">What happens next?</h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">1.</span>
                      <span>You'll receive a confirmation email within 2-3 days</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">2.</span>
                      <span>We'll share payment and shipping details (Early access: ₹599)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">3.</span>
                      <span>Your Baitho deck ships in December 2025</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">4.</span>
                      <span>Perfect timing for holiday celebrations!</span>
                    </li>
                  </ul>
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-mustard hover:bg-[#D4A43D] text-gray-900 font-bold"
                  disabled={loading}
                >
                  {loading ? 'Joining Waitlist...' : 'Join the Waitlist'}
                </Button>

                <p className="text-xs text-center text-gray-500">
                  By joining, you agree to receive updates about Baitho. No spam, we promise!
                </p>
              </form>
            </CardContent>
          </Card>

          {/* What You'll Get */}
          <Card className="mt-8 shadow-lg border-0">
            <CardHeader>
              <CardTitle className="text-xl">What You'll Get</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <Package className="w-6 h-6 text-terracotta flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-medium text-gray-900">Baitho Card Deck</h4>
                  <p className="text-sm text-gray-600">150+ conversation cards in a premium box</p>
                </div>
              </div>
              <div className="flex gap-4">
                <Package className="w-6 h-6 text-terracotta flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-medium text-gray-900">Early Access Pricing</h4>
                  <p className="text-sm text-gray-600">
                    <span className="font-bold text-lg">₹599</span> <span className="line-through text-gray-400">₹899</span> - Special pricing for waitlist members
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <Package className="w-6 h-6 text-terracotta flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-medium text-gray-900">Free Shipping</h4>
                  <p className="text-sm text-gray-600">Delivered to your doorstep at no extra cost</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
